import React, {createContext, useContext, useEffect, useState} from 'react';
import {getAuth, onAuthStateChanged, User} from 'firebase/auth';
import {httpsCallable} from 'firebase/functions';
import {firebaseFunctions} from '../api/firebase';
import {getViewModeFromRoute} from '../utils/auth/routeUtils';

interface AuthContextProps {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean | null; // Add this state to track admin status
  isAdminView: boolean; // Track current view mode for admins
  setIsAdminView: (isAdminView: boolean) => void; // Function to toggle view
  refreshAdminStatus: () => Promise<void>; // Function to refresh admin status
}

const AuthContext = createContext<AuthContextProps>({
  user: null,
  isLoading: true,
  isAdmin: null,
  isAdminView: true, // Default to admin view for admins
  setIsAdminView: () => {},
  refreshAdminStatus: async () => {},
});

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null); // Add admin status
  const [isAdminView, setIsAdminView] = useState<boolean>(true); // Admin view state

  const checkAdminStatus = React.useCallback(async () => {
    const checkStatus = httpsCallable<{isAdmin: boolean}>(
      firebaseFunctions,
      'onCheckAuth',
    );
    try {
      const response = await checkStatus();
      //@ts-ignore
      const isAdminStatus = response.data.isAdmin;
      setIsAdmin(isAdminStatus); // Set the admin status

      // Set the appropriate view mode based on current route and admin status
      const currentPath = window.location.pathname;
      const viewMode = getViewModeFromRoute(currentPath, isAdminStatus);
      setIsAdminView(viewMode);

      return isAdminStatus;
    } catch (error) {
      // For users in UNKNOWN_COMPANY, they might not have claims yet
      // Default to false but don't treat as error
      setIsAdmin(false);
      setIsAdminView(false);
      return false;
    }
  }, []);

  const refreshAdminStatus = React.useCallback(async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (currentUser) {
      await checkAdminStatus();
    }
  }, [checkAdminStatus]);

  useEffect(() => {
    const auth = getAuth();

    // Check the authentication state on app initialization
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setIsLoading(true); // Set loading to true until we verify admin status

      if (currentUser) {
        // Perform the admin check after the user is authenticated
        try {
          await checkAdminStatus();
          setUser(currentUser);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
          // Set employee view for non-admin users
          setIsAdminView(false);
        }
      } else {
        setIsAdmin(null); // Reset admin status when user is null
        setIsAdminView(true); // Reset to default admin view
      }

      setIsLoading(false); // Set loading to false after the admin check completes
    });

    return () => unsubscribe(); // Clean up the listener on unmount
  }, [checkAdminStatus]);

  // Handle route changes after initial render
  useEffect(() => {
    if (!isLoading && user && isAdmin !== null) {
      const currentPath = window.location.pathname;
      const viewMode = getViewModeFromRoute(currentPath, isAdmin);
      setIsAdminView(viewMode);
    }
  }, [user, isAdmin, isLoading]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAdmin,
        isAdminView,
        setIsAdminView,
        refreshAdminStatus,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
